import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import adminApi from '../../services/adminApi';

interface TicketMessage {
  id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
}

interface Ticket {
  id: string;
  user_id: string;
  user_email?: string;
  first_name?: string;
  last_name?: string;
  subject: string;
  category: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
  messages?: TicketMessage[];
}

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_COLORS: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
  open: 'info', in_progress: 'warning', resolved: 'success', closed: 'default',
};

const SupportTicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: page + 1, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const res = await adminApi.get('/support/tickets', { params });
      setTickets(res.data.data.tickets || []);
      setTotal(res.data.data.total || 0);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load tickets.');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openTicket = async (id: string) => {
    try {
      const res = await adminApi.get(`/support/tickets/${id}`);
      setSelected(res.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load the ticket.');
    }
  };

  const updateStatus = async (status: string) => {
    if (!selected) return;
    try {
      setSubmitting(true);
      await adminApi.put(`/support/tickets/${selected.id}/status`, { status });
      await openTicket(selected.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not update status.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    try {
      setSubmitting(true);
      await adminApi.post(`/support/tickets/${selected.id}/messages`, { message: reply.trim() });
      setReply('');
      await openTicket(selected.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not send the reply.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Support Tickets</Typography>
        <TextField select size="small" label="Status" value={statusFilter} sx={{ minWidth: 180 }}
          onChange={e => { setPage(0); setStatusFilter(e.target.value); }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
        </TextField>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map(t => (
                  <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => openTicket(t.id)}>
                    <TableCell>{t.subject}</TableCell>
                    <TableCell>{t.user_email || t.user_id}</TableCell>
                    <TableCell>{t.category}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t.status.replace('_', ' ')} color={STATUS_COLORS[t.status] || 'default'} />
                    </TableCell>
                    <TableCell>{new Date(t.updated_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {tickets.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center">No tickets found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={total} page={page} rowsPerPage={20}
            rowsPerPageOptions={[20]} onPageChange={(_, p) => setPage(p)} />
        </Paper>
      )}

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {selected?.subject}
          <Typography variant="body2" color="text.secondary">
            {selected?.user_email} ({selected?.first_name} {selected?.last_name}) - {selected?.category}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {STATUSES.map(s => (
              <Chip key={s} label={s.replace('_', ' ')} clickable
                color={selected?.status === s ? (STATUS_COLORS[s] || 'default') : 'default'}
                variant={selected?.status === s ? 'filled' : 'outlined'}
                disabled={submitting}
                onClick={() => updateStatus(s)} />
            ))}
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={1.5}>
            {(selected?.messages || []).map(m => (
              <Box key={m.id} sx={{
                alignSelf: m.sender_type === 'admin' ? 'flex-end' : 'flex-start',
                bgcolor: m.sender_type === 'admin' ? 'primary.light' : 'grey.100',
                borderRadius: 2, px: 2, py: 1, maxWidth: '85%',
              }}>
                <Typography variant="caption" color="text.secondary">
                  {m.sender_type === 'admin' ? 'Support (you)' : 'User'} - {new Date(m.created_at).toLocaleString()}
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{m.message}</Typography>
              </Box>
            ))}
          </Stack>
          {selected && selected.status !== 'closed' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1}>
                <TextField value={reply} onChange={e => setReply(e.target.value)} fullWidth size="small"
                  placeholder="Write an update to the user" inputProps={{ maxLength: 5000 }} multiline maxRows={4} />
                <Button variant="contained" onClick={sendReply} disabled={submitting || !reply.trim()}>Send</Button>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportTicketsPage;
